import { useState } from "react";

import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { AdditionalInformationFormData } from "@/Common/Models/ManualLabResultsEntry/AdditionalInformationFormData";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { TextboxControl } from "../Inputs/TextboxControl";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "../Search/PersonSearchAutocompleteCustom";
import { ResultsDateSentForm } from "./ResultsDateSentForm";

import styles from "./AdditionalInformationForm.module.scss";

interface AdditionalInformationFormProps {
    dataCy?: string;
    data: AdditionalInformationFormData;
    onChange: (mutation: (p: AdditionalInformationFormData) => void) => void;
    clinicianSearchStrategy: ClinicianSearchStrategy;
}

function AdditionalInformationForm({ data, dataCy, onChange, clinicianSearchStrategy }: Readonly<AdditionalInformationFormProps>): JSX.Element {
    const { getText } = useTranslation();
    const [requestByInputTouched, setRequestByInputTouched] = useState<boolean>(false);

    return (
        <div className={styles.container} data-cy={dataCy}>
            <div className={styles.origin}>
                <Label
                    label={getText("OriginLabel")}
                    htmlFor="originInput"
                />
                <TextboxControl
                    value={data.origin}
                    onChange={value => onChange(p => p.origin = value)}
                    dataCy="additional-info-origin-input"
                    maxLength={50}
                    id="originInput"
                />
            </div>

            <div
                className={styles.requestNumber}
            >
                <Label
                    label={getText("ManualResults_RequestNoLabel")}
                    htmlFor="requestNumberInput"
                />
                <TextboxControl
                    value={data.requestNo}
                    onChange={value => onChange(p => p.requestNo = value)}
                    dataCy="request-number-input"
                    maxLength={50}
                    id="requestNumberInput"
                />
            </div>

            <div
                className={styles.prescriber}
            >
                <Label
                    label={getText("RequestedByLabel")}
                    required={true}
                    htmlFor="prescriberInput"
                />
                <PersonSearchAutocompleteCustom
                    dataCy="additional-info-prescriber-input"
                    componentId="prescriberInput"
                    allowMultipleItemSelection={false}
                    selectedItemList={data.prescribers.toImmutable()}
                    onSelectionCallback={selection => onChange(p => p.prescribers = [selection.Item])}
                    onClearCallback={item => onChange(p => p.prescribers = data.prescribers.filter(p => p.ContactId !== item.Item.ContactId))}
                    closeDropDownAfterSelection={true}
                    valid={requestByInputTouched ? data.prescribers.length === 1 : true}
                    disabled={!clinicianSearchStrategy}
                    onSearch={searchText => {
                        setRequestByInputTouched(true);
                        return clinicianSearchStrategy.executeSearch(searchText);
                    }}
                />
            </div>

            <div
                className={styles.cc}
            >
                <Label
                    label={getText("ManualResults_CCLabel")}
                    htmlFor="ccInput"
                />
                <PersonSearchAutocompleteCustom
                    dataCy="additional-info-cc-input"
                    componentId="ccInput"
                    allowMultipleItemSelection={false}
                    selectedItemList={data.cc.toImmutable()}
                    searchStrategy={clinicianSearchStrategy}
                    onSelectionCallback={selection => onChange(p => p.cc = [selection.Item])}
                    onClearCallback={() => onChange(p => p.cc = [])}
                    closeDropDownAfterSelection={true}
                    disabled={!clinicianSearchStrategy}
                />
            </div>
            <FeatureFlagControl
                activeFeature={(
                    <ResultsDateSentForm
                        resultSentDateTime={data.resultSentDateTime}
                        onChange={date => onChange(p => p.resultSentDateTime = date)}
                    />
                )}
                fallbackFeature={(null)}
                flag={cacheStore.ClinicSettings.EnableManualResultsDateSentField}
            />
        </div>
    );
}

export { AdditionalInformationForm };
